import VOptionSet from "./vOptionSet";
import {
  IDefaultVOption,
  TVOptionSet,
  TVOptionSetManagerInput,
  TVOptionSetPrimitive
} from "../../interfaces/vSelectInterfaces";
import {Hash} from "~/interfaces/hashInterfaces"


export default class VOptionSetManager<T extends Hash = object> {
  _rawOptions: TVOptionSetManagerInput = null;
  sets: VOptionSet<T>[] = [];
  setIndex: number = null;
  optionIndex: number = null;
  lastSelectedSet: VOptionSet<T> = null;
  lastSelectedOption: any = null;
  selectedOptionIds: string[] = [];
  multiselect: boolean = false;
  searchStatus: string = "Search for results...";


  constructor(rawOptions: TVOptionSetManagerInput, multiselect: boolean = false, selectedOptionIds: string | string[] = []) {
    this.load(rawOptions, multiselect, selectedOptionIds);
  }

  load(rawOptions: TVOptionSetManagerInput, multiselect: boolean = this.multiselect, selectedOptionIds: string | string[] = this.selectedOptionIds) {
    this._rawOptions = rawOptions;
    this.buildSets(rawOptions);
    this.multiselect = multiselect;

    if (Array.isArray(selectedOptionIds)) {
      this.selectedOptionIds = selectedOptionIds;
    } else {
      this.selectedOptionIds = [selectedOptionIds];
    }
  }

  get hasIndex(): boolean {
    return this.setIndex !== null && this.optionIndex !== null;
  }

  get selectedValue(): any {
    return this.lastSelectedOption[this.sets[this.setIndex].valueKey];
  }

  get selectedLabel(): string {
    let label = '';
    if (typeof this.lastSelectedSet.labelKey === 'function') {
      label = this.lastSelectedSet.labelKey(this.lastSelectedOption);
    } else {
      label = this.lastSelectedSet.getLabel(this.lastSelectedOption);
    }
    if (label) {
      return label.trim();
    } else {
      return '';
    }
  }

  get selectedLabelSuffix(): string {
    return this.lastSelectedOption?.labelSuffix
  }

  get selectedIcon(): string {
    return this.lastSelectedSet?.icon;
  }

  get selectedPlaceholder(): string {
    let placeholder = '';
    if (this.lastSelectedSet?.placeholder) {
      placeholder = this.lastSelectedSet.getPlaceholder(this.lastSelectedOption);
    }
    return placeholder?.trim() || ''
  }

  get hasOptions(): boolean {
    return this.sets.some(set => set.hasOptions);
  }

  get hasPinnedOptions(): boolean {
    return this.sets.some(set => set.isPinned);
  }

  initializeSelectedOption(value = null): void {
    for (let i = 0; i < this.sets.length; i++) {
      const set = this.sets[i];
      if (value !== null) {
        const match = set.options.find(o => set.valueComparer ? set.valueComparer(o[set.valueKey], value) : o[set.valueKey] === value);
        if (match) {
          this.setLastSelected(match, set);
          this.confirmLastSelected(set.selectedOption, set);
          break;
        }
      }

      if (set.selectedOption) {
        this.setLastSelected(set.selectedOption, set);
        this.confirmLastSelected(set.selectedOption, set);
        break;
      }
    }
  }

  setLastSelectedFromHighlight(): any {
    const option = this.sets[this.setIndex].options[this.optionIndex];
    this.setLastSelected(option);
    return option;
  }

  confirmLastSelected(option: any = null, set: VOptionSet<T> = this.findSetByOption(option)): void {
    let id = null;
    if (option && set) {
      id = [option[set.valueKey], option[set.altKey]];
    } else {
      id = [this.lastSelectedOption[this.findSetByOption(this.lastSelectedOption).valueKey], this.lastSelectedOption[this.findSetByOption(this.lastSelectedOption).altKey]];
    }

    if (this.multiselect) {
      this.selectedOptionIds.push(id[0]);
    } else {
      this.selectedOptionIds[0] = id[1] !== undefined ? id[1] : id[0];
    }
  }

  isSelected(option: any): boolean {
    const set = this.findSetByOption(option);
    if (set) {
      return this.selectedOptionIds.includes(option[set.valueKey]) || this.selectedOptionIds.includes(option[set.altKey]);
    } else {
      return false;
    }
  }

  setHighlightedIndex(setIndex: number = null, optionIndex: number = null): boolean {
    this.setIndex = setIndex;
    this.optionIndex = optionIndex;
    return true;
  }

  isHighlighted(setIndex: number = null, optionIndex: number = null): boolean {
    return this.setIndex === setIndex && this.optionIndex === optionIndex;
  }

  highlightFirst(): boolean {
    if (!this.hasOptions) {
      return false;
    } else {
      this.setIndex = this.sets.indexOf(this.sets.find(s => s.hasOptions));
      this.optionIndex = 0;
    }
  }

  highlightNext(): boolean {
    if (!this.hasOptions) return false;

    if (this.optionIndex < this.highlightedSetOptionsLastIndex) {
      this.optionIndex++;
    } else if (this.setIndex < this.lastSetIndex) {
      this.setIndex++;
      this.optionIndex = 0;
      if (!this.sets[this.setIndex].hasOptions) this.highlightNext();
    } else {
      this.setIndex = 0;
      this.optionIndex = 0;
    }

    return true;
  }

  highlightPrevious(): boolean {
    if (!this.hasOptions) return false;

    if (this.optionIndex > 0) {
      this.optionIndex--;
    } else if (this.setIndex > 0) {
      this.setIndex--;
      if (this.sets[this.setIndex].hasOptions) {
        this.optionIndex = this.highlightedSetOptionsLastIndex;
      } else {
        this.highlightPrevious();
      }
    } else {
      this.setIndex = this.lastSetIndex;
      if (this.sets[this.setIndex].hasOptions) {
        this.optionIndex = this.highlightedSetOptionsLastIndex;

      } else {
        this.highlightPrevious();
      }
    }

    return true;
  }

  search(q: string): Promise<any> {
    if (q.length === 0) {
      this.searchStatus = "Search for results";
    } else {
      this.searchStatus = "Searching...";
    }

    const promises = [];
    this.sets.forEach(set => {
      const promise = set.search(q);
      promises.push(promise);
      promise.then(results => {
        this.highlightFirst();
      });
    });

    return Promise.all(promises).then((response) => {
      if (!this.hasOptions) {
        this.searchStatus = "No matching results...";
      }
      return response;
    });
  }

  unsetLastSelected(): void {
    this.removeLastSelected();
    this.nullifyLastSelected();
  }

  nullifyLastSelected() {
    this.lastSelectedOption = null;
    this.lastSelectedSet = null;
  }

  removeLastSelected(): void {
    const index = this.selectedOptionIds.indexOf(this.lastSelectedOption[this.lastSelectedSet.valueKey])
    this.selectedOptionIds.splice(index, 1);
  }

  resetOptions(): void {
    this.sets.forEach(set => set.resetData());
  }

  private get lastSetIndex(): number {
    return this.sets.length - 1;
  }

  private get highlightedSet(): VOptionSet<T> {
    return this.sets[this.setIndex];
  }

  private get highlightedOption(): any {
    return this.highlightedSet.options[this.optionIndex];
  }

  private get highlightedSetOptionsLastIndex(): number {
    return this.highlightedSet.options.length - 1;
  }

  private get areAllSelected(): boolean {
    return this.sets.every(s => s.options.every(o => this.selectedOptionIds.includes(o[s.valueKey])));
  }

  private buildSets(options: TVOptionSetManagerInput): void {
    if (VOptionSetManager.isDefaultVOptionSet(options)) {
      this.sets = [new VOptionSet({options: options})];
    } else if (VOptionSetManager.isVOptionSets(options)) {
      this.sets = options.map((optionOrSet) => {
        return (optionOrSet instanceof VOptionSet) ? optionOrSet : new VOptionSet(optionOrSet);
      });
    } else if (VOptionSetManager.isPrimitiveVOptionSet(options)) {
      this.sets = [new VOptionSet({
        options: options.map(option => {
          return {label: option.toString(), value: option};
        })
      })];
    } else if (VOptionSetManager.isSingleVOptionSet(options)) {
      this.sets = [new VOptionSet(options)];
    }
  }

  private static isVOptionSets(obj: any): obj is TVOptionSet[] {
    if (!Array.isArray(obj)) {
      return false;
    }
    return obj.every(x => typeof x === 'object' && x.hasOwnProperty('endpoint') || x.hasOwnProperty('options'));
  }

  private static isSingleVOptionSet(obj: any): obj is TVOptionSet {
    return !Array.isArray(obj) && typeof obj === 'object' && obj.hasOwnProperty('endpoint') || obj.hasOwnProperty('options');
  }

  private static isDefaultVOptionSet(obj: any): obj is IDefaultVOption[] {
    if (!Array.isArray(obj)) {
      return false;
    }
    return obj.every(set => {
      return typeof set === 'object' &&
        !set.hasOwnProperty('endpoint') && !set.hasOwnProperty('options') &&
        set.hasOwnProperty('label') && set.hasOwnProperty('value');
    });
  }

  private static isPrimitiveVOptionSet(obj: any): obj is TVOptionSetPrimitive {
    if (!Array.isArray(obj)) {
      return false;
    }
    return obj.every(x => x !== undefined && typeof x !== 'object');
  }

  private setLastSelected(option: any, set: VOptionSet<T> = null): void {
    this.lastSelectedOption = option;
    this.lastSelectedSet = set ? set : this.findSetByOption(option);
  }

  private findSetByOption(option: any = null): VOptionSet<T> {
    if (!option) return;
    return this.sets.find(set => {
      const optionValue = option[set.valueKey];
      return set.options.some(opt => opt[set.valueKey] === optionValue);
    });
  }
}
